import { FC, Fragment, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  countGiftBrandSellerDeliveries,
  desiredDeliveryDateError,
  listGiftBrandSellerDeliveries,
  updateGiftDeliveryDesiredDate,
  updateGiftDeliveryRecipient,
  updateGiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  GiftDeliveryForAdmin,
  GiftDeliveryStatus,
  GiftDeliveryType,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { useGiftCsMemosByOrders } from '@shared/api/giftCsMemo/giftCsMemo.hook';
import { giftItemBrandListDataAtom } from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { recipientRegionPatch } from '@shared/utils/giftAddressRegion';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { useAsyncFn } from 'react-use';
import {
  ActiveDeliveryDatePolicy,
  deliveryDateRelativeLabel,
  formatDeliveryDateShort,
  resolveActiveDatePolicy,
  toDeliveryDateKey,
} from '@service/giftStore/utils/giftDeliveryDate.util';

import {
  nextStatusLabel,
  nextStatusMap,
  prevStatusLabel,
  prevStatusMap,
  statusLabel,
  typeLabel,
} from '../../giftSeller/utils/delivery.constants';
import {
  formatPhone,
  formatRelativeDateTime,
  formatShortDateTime,
  getBrandItemName,
  getOrdererInfo,
  isoToKstInput,
  nowForInput,
} from '../../giftSeller/utils/delivery.utils';
import { CsMemoButton } from '../../giftSeller/components/csMemo';
import {
  DesiredDeliveryDateBadge,
  desiredDeliveryDateTone,
} from '../../giftSeller/components/DesiredDeliveryDateBadge';
import {
  DesiredDeliveryDateEditModal,
  DesiredDeliveryDateModalTarget,
} from '../../giftSeller/components/DesiredDeliveryDateEditModal';
import { RecipientEditModal } from '../../giftSeller/components/RecipientEditModal';
import { RecipientModalState } from '../../giftSeller/utils/delivery.types';
import {
  canAddThanksCard,
  GiftBrandThanksCardModal,
} from '../components/GiftBrandThanksCardModal';
import { BrandCsMemoButton } from '../components/csMemo/BrandCsMemoButton';
import { exportDeliveryOrderSheetExcel } from '../utils/deliveryOrderExcel';

// 송장 입력 모달 상태
// - mode 'advance': 준비중 -> 배송중 전환과 함께 송장 입력 (발송시각=현재)
// - mode 'edit'   : 이미 배송중/배송완료인 건의 송장만 수정 (상태/발송·완료시각 보존)
interface ShippingModalState {
  deliveryId: string;
  carrier: string;
  trackingNumber: string;
  mode: 'advance' | 'edit';
  // edit 모드에서 유지할 현재 상태 및 보존할 기존 시각 (ISO)
  status?: GiftDeliveryStatus;
  shippedAt?: string;
  deliveredAt?: string;
}

// 배송완료 처리 모달 상태 (배송중 -> 배송완료, 또는 완료건의 완료시각 수정)
interface DeliverModalState {
  deliveryId: string;
  deliveredAt: string; // datetime-local 입력값 (YYYY-MM-DDTHH:mm)
  isEdit?: boolean; // 이미 배송완료된 건의 완료시각만 수정하는 경우 true
  bulkIds?: string[]; // 있으면 여러 건에 동일 완료시각 일괄 적용
}

// KST 시각 유틸(nowForInput/isoToKstInput/formatRelativeDateTime/formatShortDateTime)은
// 에이전시 배송관리 페이지와 공용으로 delivery.utils 에서 가져온다.

const DT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^(\d{2}):(\d{2})$/;
// 입력 숫자(HHmm)를 'HH:mm' 마스크로 정렬
const maskTime = (raw: string) => {
  const d = raw.replace(/\D/g, '').slice(0, 4);
  return d.length <= 2 ? d : `${d.slice(0, 2)}:${d.slice(2, 4)}`;
};

/**
 * 날짜는 캘린더(antd DatePicker)로 선택, 시간은 'HH:mm' 직접 입력.
 * value/onChange 는 내부 'YYYY-MM-DDTHH:mm' 문자열(한국 시각 벽시계) 사용.
 */
const KstDateTimeField: FC<{
  value: string;
  onChange: (v: string) => void;
}> = ({ value, onChange }) => {
  const m = DT_RE.exec(value) ?? DT_RE.exec(nowForInput());
  const dateStr = m ? `${m[1]}-${m[2]}-${m[3]}` : '';
  const timeStr = m ? `${m[4]}:${m[5]}` : '';

  const [timeText, setTimeText] = useState(timeStr);
  // 모달이 새로 열리거나 외부에서 값이 바뀌면 시간 표시값 동기화
  useEffect(() => setTimeText(timeStr), [timeStr]);

  // 날짜+시간이 모두 유효할 때만 상위로 반영
  const emit = (d: string, t: string) => {
    if (!DATE_RE.test(d) || !TIME_RE.test(t)) return;
    onChange(`${d}T${t}`);
  };

  return (
    <div className="flex flex-nowrap items-center gap-2">
      <DatePicker
        value={dateStr ? dayjs(dateStr) : null}
        format="YYYY-MM-DD"
        allowClear={false}
        inputReadOnly
        onChange={(d) => d && emit(d.format('YYYY-MM-DD'), timeText)}
        className="w-36 shrink-0"
        size="large"
      />
      <input
        type="text"
        inputMode="numeric"
        value={timeText}
        placeholder="HH:mm"
        maxLength={5}
        onChange={(e) => {
          const masked = maskTime(e.target.value);
          setTimeText(masked);
          emit(dateStr, masked);
        }}
        onFocus={(e) => e.target.select()}
        onBlur={() => setTimeText(timeStr)}
        className="w-32 rounded-md border border-gray-300 px-2 py-2 text-center text-base focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      />
      <span className="shrink-0 text-xs text-gray-500">시:분</span>
    </div>
  );
};

// 필터 칩으로 노출되는 전체 상태 (배송완료 포함)
const FILTERABLE_STATUSES = [
  GiftDeliveryStatus.WaitingAddress,
  GiftDeliveryStatus.AddressSubmitted,
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
  GiftDeliveryStatus.Delivered,
  // 취소 건도 목록에 포함되어 감사카드 조회가 가능하도록 필터 노출
  GiftDeliveryStatus.Canceled,
];

// 기본 선택: 진행중 상태만. 주소입력대기는 배송지 미정이라 기본 제외(칩으로는 선택 가능)
const DEFAULT_STATUSES: GiftDeliveryStatus[] = [
  GiftDeliveryStatus.AddressSubmitted,
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
];

// 칩에 건수를 노출할 "처리 대기/진행중" 상태 (완료·취소는 누적이라 제외)
const COUNTABLE_STATUSES: GiftDeliveryStatus[] = [
  GiftDeliveryStatus.WaitingAddress,
  GiftDeliveryStatus.AddressSubmitted,
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
];

/** 배송이 속한 주문 id (populate 여부와 무관하게 뽑는다) */
const getOrderId = (d: GiftDeliveryForAdmin): string =>
  typeof d.giftOrder === 'object' ? d.giftOrder?._id ?? '' : d.giftOrder ?? '';

/** CS 메모장 부제 — 어느 건의 응대인지 구분 */
const csMemoSubtitle = (d: GiftDeliveryForAdmin): string =>
  `${d.recipient?.name ?? '수령인 미상'} · ${getBrandItemName(d)}`;

// 발주를 기다리는 "신규 주문" 상태 (배송지 입력이 끝나 바로 발주 가능한 건)
const NEW_ORDER_STATUS = GiftDeliveryStatus.AddressSubmitted;

// 받는 날을 지정·수정할 수 있는 상태 = 아직 배송이 끝나지 않은 건 (완료·취소·주소입력대기 제외)
const DESIRED_DATE_EDITABLE_STATUSES: GiftDeliveryStatus[] = [
  GiftDeliveryStatus.AddressSubmitted,
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
];

// 체크 선택이 가능한 상태 = 배송지가 확정된 건.
// 발주서 엑셀을 다시 뽑을 수 있어야 하므로 이미 처리한 준비중·배송중·배송완료도 포함한다.
// 주소입력대기(주소 없음)·취소는 발주 대상이 아니라 제외.
const SELECTABLE_STATUSES: GiftDeliveryStatus[] = [
  GiftDeliveryStatus.AddressSubmitted,
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
  GiftDeliveryStatus.Delivered,
];

export const GiftBrandDeliveryManagePage: FC = function GiftBrandDeliveryManagePage() {
  // 여러 상태를 동시에 선택 가능 (다중 선택)
  const [statusFilter, setStatusFilter] =
    useState<GiftDeliveryStatus[]>(DEFAULT_STATUSES);
  const [searchText, setSearchText] = useState('');
  // admin 전용: 브랜드(업체)별 필터 ('all' = 전체, NO_BRAND = 빈업체)
  const NO_BRAND = '__none__';
  const [brandFilter, setBrandFilter] = useState<string>('all');
  // 받는 날(배송 희망일) 필터 — 'all' 또는 'YYYY-MM-DD'. 날짜 지정 주문이 목록에 있을 때만 칩이 뜬다
  const [desiredDateFilter, setDesiredDateFilter] = useState<string>('all');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [revertingId, setRevertingId] = useState<string | null>(null);
  // 다중 선택: 주소입력완료 건(일괄 발주+엑셀) / 배송완료 건(완료시각 일괄 변경)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [shippingModal, setShippingModal] = useState<ShippingModalState | null>(null);
  const [deliverModal, setDeliverModal] = useState<DeliverModalState | null>(null);
  const [cardDelivery, setCardDelivery] = useState<GiftDeliveryForAdmin | null>(null);
  // 배송지 정정 (admin 전용 — 전화로 정정 요청이 온 건을 운영자가 직접 고친다)
  const [recipientModal, setRecipientModal] = useState<RecipientModalState | null>(null);
  // 받는 날 지정·수정 (관리자·배송업체 공통 — 구매자 변경 요청은 전화로 받아 여기서 고친다)
  const [desiredDateTarget, setDesiredDateTarget] =
    useState<DesiredDeliveryDateModalTarget | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Admin/GiftAgent는 전 브랜드(admin), 브랜드셀러는 본인 브랜드(gift-brand-seller)로 조회
  const { giftBrandRole } = useAuth();
  const role = giftBrandRole() as LowerRole.Admin | LowerRole.GiftBrandSeller;
  const isAdminView = role === LowerRole.Admin;

  // 감사카드 템플릿 (templateId 매칭용). Admin/GiftAgent는 전 브랜드, 셀러는 본인 브랜드.
  // 셀러가 여러 브랜드를 담당할 수 있으므로 모든 담당 브랜드의 템플릿을 합쳐 매칭한다.
  useGiftItemBrandListHook(role);
  const brands = useRecoilValue(giftItemBrandListDataAtom);
  const thanksTemplates = useMemo(
    () => (brands ?? []).flatMap((b) => b.thanksPrint?.templates ?? []),
    [brands],
  );
  // 브랜드별 배송 희망일 정책 (켜진 브랜드만) — 날짜 없는 건에 [받는 날 지정]을 띄울지, 수정 모달의 휴무 경고
  const datePolicyByBrand = useMemo(() => {
    const map = new Map<string, ActiveDeliveryDatePolicy>();
    for (const b of brands ?? []) {
      const policy = resolveActiveDatePolicy(b.deliveryDetail?.datePolicy);
      if (policy) map.set(b._id, policy);
    }
    return map;
  }, [brands]);
  const datePolicyOf = (d: GiftDeliveryForAdmin) =>
    datePolicyByBrand.get(
      typeof d.giftItemBrand === 'object'
        ? d.giftItemBrand?._id ?? ''
        : d.giftItemBrand ?? '',
    ) ?? null;

  const [{ loading, error: deliveryError, value: deliveries }, fetchDeliveries] =
    useAsyncFn(
      async (statuses: GiftDeliveryStatus[]) => {
        // 선택된 상태가 없으면 빈 목록 (전체 이력 로드 방지)
        if (!statuses.length) return [];
        return listGiftBrandSellerDeliveries(
          { deliveryStatus: { $in: statuses } },
          { createdAt: -1 },
          role,
        );
      },
      [role],
    );

  // 칩 뱃지용 상태별 건수. 선택 필터와 무관하게 항상 전체 대기 물량을 보여준다.
  const [{ value: statusCounts }, fetchCounts] = useAsyncFn(async () => {
    const entries = await Promise.all(
      COUNTABLE_STATUSES.map(
        async (s) =>
          [s, await countGiftBrandSellerDeliveries({ deliveryStatus: s }, role)] as const,
      ),
    );
    return Object.fromEntries(entries) as Partial<Record<GiftDeliveryStatus, number>>;
  }, [role]);

  // 목록 + 건수를 함께 갱신 (상태 변경 후/새로고침에서 사용)
  const refreshAll = () => Promise.all([fetchDeliveries(statusFilter), fetchCounts()]);

  useEffect(() => {
    fetchDeliveries(statusFilter);
    fetchCounts();
  }, [fetchDeliveries, fetchCounts, statusFilter]);

  // 발주 대기 중인 신규 주문 건수 (배너/강조 트리거)
  const newOrderCount = statusCounts?.[NEW_ORDER_STATUS] ?? 0;

  const resetFilters = () => {
    setStatusFilter(DEFAULT_STATUSES);
    setBrandFilter('all');
    setSearchText('');
    setDesiredDateFilter('all');
  };

  const hasCustomFilters =
    searchText.trim() !== '' ||
    brandFilter !== 'all' ||
    desiredDateFilter !== 'all' ||
    statusFilter.length !== DEFAULT_STATUSES.length ||
    DEFAULT_STATUSES.some((status) => !statusFilter.includes(status));

  const toggleStatus = (s: GiftDeliveryStatus) =>
    setStatusFilter((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );

  const copyAddress = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      alert('복사에 실패했습니다.');
    }
  };

  // 배송지 정정 모달 열기 (admin 전용). 이미 발송된 건도 열리되 모달에서 경고한다
  const openRecipientEdit = (delivery: GiftDeliveryForAdmin) => {
    const r = delivery.recipient;
    setRecipientModal({
      deliveryId: delivery._id,
      name: r?.name ?? '',
      phoneNumber: r?.phoneNumber ?? '',
      postalCode: r?.postalCode ?? '',
      address: r?.address ?? '',
      addressDetail: r?.addressDetail ?? '',
      isSaving: false,
    });
  };

  const saveRecipient = async () => {
    if (!recipientModal) return;
    if (!recipientModal.address.trim()) {
      alert('주소를 입력해주세요.');
      return;
    }
    setRecipientModal((prev) => (prev ? { ...prev, isSaving: true } : null));
    try {
      // 빈 문자열도 그대로 보낸다(상세주소를 지우는 정정이 있다). 미전송은 undefined 뿐.
      await updateGiftDeliveryRecipient(recipientModal.deliveryId, {
        name: recipientModal.name.trim(),
        phoneNumber: recipientModal.phoneNumber.replace(/-/g, '').trim(),
        postalCode: recipientModal.postalCode.trim(),
        address: recipientModal.address.trim(),
        addressDetail: recipientModal.addressDetail.trim(),
        // 주소를 재검색한 경우에만 카카오 원문(권역 배송비 근거)이 함께 온다
        ...recipientRegionPatch(recipientModal.region),
      });
      setRecipientModal(null);
      await refreshAll();
    } catch (err) {
      console.error(err);
      alert('배송지 수정에 실패했습니다.');
      setRecipientModal((prev) => (prev ? { ...prev, isSaving: false } : null));
    }
  };

  const openDesiredDateEdit = (delivery: GiftDeliveryForAdmin) =>
    setDesiredDateTarget({
      deliveryId: delivery._id,
      subtitle: csMemoSubtitle(delivery),
      currentDate: delivery.desiredDeliveryDate,
      requestedDate: delivery.desiredDeliveryDateRequested,
      logs: delivery.desiredDeliveryDateLogs,
      bulk: delivery.type === GiftDeliveryType.Bulk,
      policy: datePolicyOf(delivery),
    });

  const saveDesiredDate = async (
    date: string,
    forcePassword?: string,
  ): Promise<boolean> => {
    if (!desiredDateTarget) return false;
    try {
      await updateGiftDeliveryDesiredDate(
        desiredDateTarget.deliveryId,
        date,
        role,
        forcePassword,
      );
      setDesiredDateTarget(null);
      await refreshAll();
      return true;
    } catch (err) {
      console.error(err);
      alert(desiredDeliveryDateError(err));
      return false;
    }
  };

  // 현재 목록에 존재하는 브랜드 목록 (필터 드롭다운용, 중복 제거)
  const brandOptions = useMemo(() => {
    const map = new Map<string, string>();
    let hasNoBrand = false;
    for (const d of deliveries ?? []) {
      if (typeof d.giftItemBrand === 'object' && d.giftItemBrand?._id) {
        map.set(d.giftItemBrand._id, d.giftItemBrand.name);
      } else {
        hasNoBrand = true;
      }
    }
    const options = Array.from(map, ([id, name]) => ({ id, name }));
    // 업체가 없는 건도 '빈업체'로 필터 가능하게 (항상 마지막)
    if (hasNoBrand) options.push({ id: NO_BRAND, name: '빈업체' });
    return options;
  }, [deliveries, NO_BRAND]);

  // 브랜드 필터/컬럼 노출 여부: admin은 항상, 셀러는 담당 브랜드가 2개 이상 섞여 있을 때만
  const showBrandUi = isAdminView || brandOptions.length > 1;

  // 받는 날이 지정된 건을 날짜별로 센다 (빠른 날짜순) — 필터 칩용
  const desiredDateOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of deliveries ?? []) {
      const key = toDeliveryDateKey(d.desiredDeliveryDate);
      if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return Array.from(counts, ([date, count]) => ({ date, count })).sort((a, b) =>
      a.date < b.date ? -1 : 1,
    );
  }, [deliveries]);
  // 고른 날짜가 목록에서 사라지면(상태 필터 변경·처리 완료) 전체로 본다
  const activeDesiredDate =
    desiredDateFilter !== 'all' &&
    desiredDateOptions.some((o) => o.date === desiredDateFilter)
      ? desiredDateFilter
      : 'all';

  const rows = useMemo(() => {
    // 상태 필터는 서버 조회(deliveryStatus $in)로 처리되므로 여기서는 검색/브랜드/받는 날만 적용
    let result = [...(deliveries ?? [])];
    if (activeDesiredDate !== 'all') {
      result = result.filter(
        (d) => toDeliveryDateKey(d.desiredDeliveryDate) === activeDesiredDate,
      );
    }
    if (showBrandUi && brandFilter !== 'all') {
      result = result.filter((d) => {
        const hasBrand = typeof d.giftItemBrand === 'object' && !!d.giftItemBrand?._id;
        return brandFilter === NO_BRAND
          ? !hasBrand
          : hasBrand &&
              typeof d.giftItemBrand === 'object' &&
              d.giftItemBrand?._id === brandFilter;
      });
    }
    if (searchText) {
      const search = searchText.trim().toLowerCase();
      const phoneSearch = search.replace(/-/g, '');
      result = result.filter((d) => {
        const phone = d.recipient?.phoneNumber?.replace(/-/g, '') || '';
        const name = d.recipient?.name?.toLowerCase() || '';
        const item = getBrandItemName(d).toLowerCase();
        return (
          (!!phoneSearch && phone.includes(phoneSearch)) ||
          name.includes(search) ||
          item.includes(search)
        );
      });
    }
    return result;
  }, [deliveries, searchText, showBrandUi, brandFilter, NO_BRAND, activeDesiredDate]);

  // 상품옵션 열: 보이는 건 중 옵션을 고른 주문이 있을 때만 (옵션 없는 상품만 파는 브랜드는 열을 숨긴다)
  const showOptionColumn = rows.some((d) => (d.selectedOptions?.length ?? 0) > 0);
  // 받는 날 표기: 보이는 건 중 날짜가 지정됐거나 날짜 지정을 지원하는 브랜드 건이 있을 때만
  const showDesiredDateUi = rows.some(
    (d) => !!toDeliveryDateKey(d.desiredDeliveryDate) || !!datePolicyOf(d),
  );

  // CS 응대 메모 — 현재 목록의 주문들을 한 번에 채운다(주문 축이라 쿠폰·배송 메모 포함).
  // 이걸 미리 채워야 어느 행에 응대 이력이 있는지 열어보지 않고도 배지로 보인다.
  // 배송 여러 건이 한 주문에 묶이므로 중복 id는 제거한다. 백엔드 미배포면 unavailable.
  const csMemoOrderIds = useMemo(
    () => Array.from(new Set(rows.map(getOrderId).filter(Boolean))),
    [rows],
  );
  // 공급사(gift-brand-seller)는 admin 배치 라우트를 못 쓴다 — 401이 떨어지면 전역
  // 인터셉터가 화면을 되돌려 작업이 끊긴다. 관리자일 때만 조회한다.
  const csMemos = useGiftCsMemosByOrders(csMemoOrderIds, isAdminView && !loading);

  // 체크 선택 가능한 행 (배송지가 확정된 건 — 발주서 엑셀 재발행 대상 포함)
  const selectableRows = useMemo(
    () => rows.filter((d) => SELECTABLE_STATUSES.includes(d.deliveryStatus)),
    [rows],
  );
  const allSelectableSelected =
    selectableRows.length > 0 && selectableRows.every((d) => selectedIds.has(d._id));
  // 선택 중인 행을 용도별로 분리 (필터 변경 등으로 목록에서 빠진 id는 자연히 제외)
  const selectedRows = useMemo(
    () => rows.filter((d) => selectedIds.has(d._id)),
    [rows, selectedIds],
  );
  const selectedNewRows = useMemo(
    () => selectedRows.filter((d) => d.deliveryStatus === NEW_ORDER_STATUS),
    [selectedRows],
  );
  const selectedDeliveredRows = useMemo(
    () => selectedRows.filter((d) => d.deliveryStatus === GiftDeliveryStatus.Delivered),
    [selectedRows],
  );

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAll = () =>
    setSelectedIds(
      allSelectableSelected ? new Set() : new Set(selectableRows.map((d) => d._id)),
    );

  // 현재 목록의 주소입력완료 건만 전체 선택 (매일 정해진 시각에 한 번에 발주하는 흐름용)
  const selectAllNew = () =>
    setSelectedIds(
      new Set(
        rows.filter((d) => d.deliveryStatus === NEW_ORDER_STATUS).map((d) => d._id),
      ),
    );

  // 선택한 배송완료 건들의 완료시각을 한 번에 변경하는 모달 열기
  const openBulkDeliveredEdit = () => {
    const ids = selectedDeliveredRows.map((d) => d._id);
    const first = ids[0];
    if (!first) return;
    setDeliverModal({
      deliveryId: first,
      bulkIds: ids,
      deliveredAt: nowForInput(),
      isEdit: true,
    });
  };

  // 발주서 엑셀 — 배송희망일 열은 화면의 받는 날 열과 같은 기준으로, 내보내는 건 중 해당이 있을 때만
  const exportOrderSheet = (targets: GiftDeliveryForAdmin[]) =>
    exportDeliveryOrderSheetExcel(targets, thanksTemplates, {
      includeDesiredDate: targets.some(
        (d) => !!toDeliveryDateKey(d.desiredDeliveryDate) || !!datePolicyOf(d),
      ),
    });

  // 선택한 행을 발주서 엑셀로 내려받기 (상태 변경 없음, 재다운로드용)
  const exportSelectedExcel = () => {
    if (!selectedRows.length) return;
    exportOrderSheet(selectedRows);
  };

  // 선택한 주소입력완료 건을 엑셀로 내려받은 뒤 상품 준비중으로 일괄 전환.
  // 엑셀을 먼저 저장해야 전환 도중 일부가 실패해도 발주 목록이 파일에 남는다.
  const bulkPrepare = async (withExcel: boolean) => {
    const targets = selectedNewRows;
    if (!targets.length) return;
    const msg = withExcel
      ? `${targets.length}건을 발주서 엑셀로 내려받고 상품 준비중으로 처리하시겠습니까?`
      : `${targets.length}건을 상품 준비중으로 처리하시겠습니까?`;
    if (!confirm(msg)) return;
    setBulkProcessing(true);
    try {
      if (withExcel) exportOrderSheet(targets);
      let fail = 0;
      for (const d of targets) {
        try {
          await updateGiftDeliveryStatus(
            d._id,
            GiftDeliveryStatus.Preparing,
            undefined,
            role,
          );
        } catch (err) {
          console.error(err);
          fail++;
        }
      }
      // 처리한 건만 선택 해제 (배송완료 선택분은 유지)
      const done = new Set(targets.map((d) => d._id));
      setSelectedIds((prev) => new Set(Array.from(prev).filter((id) => !done.has(id))));
      await refreshAll();
      if (fail) {
        alert(`상품 준비중 처리 실패 ${fail}건 (성공 ${targets.length - fail}건)`);
      }
    } finally {
      setBulkProcessing(false);
    }
  };

  // 다음 단계로 상태 변경
  const advanceStatus = async (delivery: GiftDeliveryForAdmin) => {
    const next = nextStatusMap[delivery.deliveryStatus];
    if (!next) return;

    // 준비중 -> 배송중 은 송장 정보를 받는다
    if (next === GiftDeliveryStatus.Shipping) {
      setShippingModal({
        deliveryId: delivery._id,
        carrier: delivery.shipping?.carrier || '',
        trackingNumber: delivery.shipping?.trackingNumber || '',
        mode: 'advance',
      });
      return;
    }

    // 배송중 -> 배송완료 는 배송완료 일시를 입력받는다 (기본값: 현재 시각)
    if (next === GiftDeliveryStatus.Delivered) {
      setDeliverModal({
        deliveryId: delivery._id,
        deliveredAt: nowForInput(),
      });
      return;
    }

    setProcessingId(delivery._id);
    try {
      await updateGiftDeliveryStatus(delivery._id, next, undefined, role);
      await refreshAll();
    } catch (err) {
      console.error(err);
      alert('상태 변경에 실패했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 이미 배송완료된 건의 완료시각만 수정 (송장번호와 무관, 상태 유지)
  const openDeliveredEdit = (delivery: GiftDeliveryForAdmin) => {
    setDeliverModal({
      deliveryId: delivery._id,
      deliveredAt: delivery.shipping?.deliveredAt
        ? isoToKstInput(delivery.shipping.deliveredAt)
        : nowForInput(),
      isEdit: true,
    });
  };

  // 이미 배송중/배송완료인 건의 송장만 추가/수정 (상태 전환 없음)
  const openShippingEdit = (delivery: GiftDeliveryForAdmin) => {
    setShippingModal({
      deliveryId: delivery._id,
      carrier: delivery.shipping?.carrier || '',
      trackingNumber: delivery.shipping?.trackingNumber || '',
      mode: 'edit',
      status: delivery.deliveryStatus,
      // 기존 발송/완료 시각을 보존 (shipping 서브도큐먼트 전체가 교체되므로)
      shippedAt: delivery.shipping?.shippedAt
        ? new Date(delivery.shipping.shippedAt).toISOString()
        : undefined,
      deliveredAt: delivery.shipping?.deliveredAt
        ? new Date(delivery.shipping.deliveredAt).toISOString()
        : undefined,
    });
  };

  const submitShipping = async () => {
    if (!shippingModal) return;
    const isEdit = shippingModal.mode === 'edit';
    // 수정 모드는 상태를 그대로 유지, 전환 모드는 배송중으로
    const targetStatus =
      isEdit && shippingModal.status ? shippingModal.status : GiftDeliveryStatus.Shipping;
    setProcessingId(shippingModal.deliveryId);
    try {
      await updateGiftDeliveryStatus(
        shippingModal.deliveryId,
        targetStatus,
        {
          carrier: shippingModal.carrier.trim() || undefined,
          trackingNumber: shippingModal.trackingNumber.trim() || undefined,
          // 전환 모드는 발송시각=현재, 수정 모드는 기존 시각 보존
          shippedAt: isEdit ? shippingModal.shippedAt : new Date().toISOString(),
          deliveredAt: isEdit ? shippingModal.deliveredAt : undefined,
        },
        role,
      );
      setShippingModal(null);
      await refreshAll();
    } catch (err) {
      console.error(err);
      alert(isEdit ? '송장 수정에 실패했습니다.' : '송장 등록에 실패했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  const submitDeliver = async () => {
    if (!deliverModal) return;
    if (!deliverModal.deliveredAt) {
      alert('배송완료 일시를 입력해주세요.');
      return;
    }
    const ids = deliverModal.bulkIds ?? [deliverModal.deliveryId];
    // 부분 입력(예: 연도 미완성) 등 잘못된 일시 방어
    const parsed = new Date(`${deliverModal.deliveredAt}:00+09:00`);
    if (!DT_RE.test(deliverModal.deliveredAt) || Number.isNaN(parsed.getTime())) {
      alert('배송완료 일시를 올바르게 입력해주세요.');
      return;
    }
    const iso = parsed.toISOString();
    const isBulk = !!deliverModal.bulkIds;
    if (isBulk) setBulkProcessing(true);
    else setProcessingId(deliverModal.deliveryId);
    try {
      let fail = 0;
      for (const id of ids) {
        try {
          await updateGiftDeliveryStatus(
            id,
            GiftDeliveryStatus.Delivered,
            { deliveredAt: iso },
            role,
          );
        } catch (err) {
          console.error(err);
          fail++;
        }
      }
      setDeliverModal(null);
      // 처리한 건만 선택 해제 (주소입력완료 선택분은 유지)
      if (isBulk) {
        const done = new Set(ids);
        setSelectedIds((prev) => new Set(Array.from(prev).filter((id) => !done.has(id))));
      }
      await refreshAll();
      if (fail) {
        alert(`완료시각 변경 실패 ${fail}건 (성공 ${ids.length - fail}건)`);
      }
    } finally {
      setBulkProcessing(false);
      setProcessingId(null);
    }
  };

  // 실수로 진행한 상태 변경을 이전 단계로 되돌린다 (admin / gift-brand-seller 공통)
  const revertStatus = async (delivery: GiftDeliveryForAdmin) => {
    const prev = prevStatusMap[delivery.deliveryStatus];
    if (!prev) return;
    const label = prevStatusLabel[delivery.deliveryStatus] || '이전 상태로 되돌리기';
    if (!confirm(`${label} 하시겠습니까?`)) return;
    setRevertingId(delivery._id);
    try {
      await updateGiftDeliveryStatus(delivery._id, prev, undefined, role);
      await refreshAll();
    } catch (err) {
      console.error(err);
      alert('상태 되돌리기에 실패했습니다.');
    } finally {
      setRevertingId(null);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold">주문 배송관리</h2>
        <p className="mt-1 text-sm text-gray-500">
          신규 주문을 발주 → 배송 → 완료 단계로 처리하세요.
        </p>
      </div>

      {/* 필터 */}
      <div className="mb-4 space-y-2">
        {/* 상태 다중 선택 칩 (대기/진행중 상태는 현재 건수를 함께 노출) */}
        <div className="flex flex-wrap items-center gap-1.5">
          {FILTERABLE_STATUSES.map((s) => {
            const active = statusFilter.includes(s);
            const count = COUNTABLE_STATUSES.includes(s) ? statusCounts?.[s] : undefined;
            const countClass = active
              ? 'bg-indigo-100 text-indigo-700'
              : 'bg-gray-100 text-gray-600';
            return (
              <button
                key={s}
                onClick={() => toggleStatus(s)}
                aria-pressed={active}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                  active
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-800'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {statusLabel[s]?.text ?? s}
                {count !== undefined && (
                  <span
                    className={`inline-flex min-w-[1.125rem] justify-center rounded-full px-1 text-[10px] font-bold leading-4 ${countClass}`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-gray-500">
          상태는 여러 개 선택할 수 있으며, 상태별 건수는 검색·브랜드 필터 적용 전
          기준입니다.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {showBrandUi && (
            <select
              aria-label="브랜드 필터"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700"
            >
              <option value="all">전체 브랜드</option>
              {brandOptions.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            aria-label="배송 검색"
            placeholder="수령인 · 연락처 · 상품명"
            className="flex-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm"
          />
          <button
            onClick={() => refreshAll()}
            disabled={loading}
            className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            {loading ? '조회 중...' : '새로고침'}
          </button>
          {hasCustomFilters && (
            <button
              onClick={resetFilters}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
            >
              필터 초기화
            </button>
          )}
        </div>
      </div>

      {/* 받는 날 지정 주문 — 날짜별 건수 칩 (누르면 그 날짜만). 날짜 지정 주문이 있을 때만 */}
      {desiredDateOptions.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
          <span className="mr-1 text-xs font-semibold text-gray-700">받는 날 지정</span>
          <button
            onClick={() => setDesiredDateFilter('all')}
            aria-pressed={activeDesiredDate === 'all'}
            className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
              activeDesiredDate === 'all'
                ? 'border-indigo-300 bg-indigo-50 text-indigo-800'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            전체
          </button>
          {desiredDateOptions.map((o) => {
            const active = activeDesiredDate === o.date;
            return (
              <button
                key={o.date}
                onClick={() => setDesiredDateFilter(active ? 'all' : o.date)}
                aria-pressed={active}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold ${
                  active
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {formatDeliveryDateShort(o.date)}
                <span
                  className={`rounded px-1 text-[10px] leading-4 ${desiredDeliveryDateTone(
                    o.date,
                  )}`}
                >
                  {deliveryDateRelativeLabel(o.date)}
                </span>
                <span className={active ? 'text-indigo-700' : 'text-gray-500'}>
                  {o.count}건
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* 신규 주문(발주 대기) 알림 배너 — 빠른 처리 유도 */}
      {newOrderCount > 0 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="rounded bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
              NEW
            </span>
            <p className="text-sm text-gray-700">
              신규 주문 <b className="tabular-nums text-indigo-700">{newOrderCount}건</b>{' '}
              접수 대기
              <span className="ml-2 text-xs text-gray-500">전체 신규 기준</span>
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {!(statusFilter.length === 1 && statusFilter[0] === NEW_ORDER_STATUS) && (
              <button
                onClick={() => setStatusFilter([NEW_ORDER_STATUS])}
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
              >
                신규만 보기 →
              </button>
            )}
            {/* 현재 목록의 신규 건을 한 번에 체크 → 아래 선택 바에서 발주+엑셀 */}
            {rows.some((d) => d.deliveryStatus === NEW_ORDER_STATUS) && (
              <button
                onClick={selectAllNew}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                신규 전체 선택
              </button>
            )}
          </div>
        </div>
      )}

      {/* 다중 선택 시 일괄 처리 바: 주소입력완료 → 발주+엑셀, 배송완료 → 완료시각 변경 */}
      {!loading && !deliveryError && selectedRows.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
          <span className="text-sm font-medium text-indigo-700">
            {selectedRows.length}건 선택됨
          </span>
          {selectedNewRows.length > 0 && (
            <>
              <button
                onClick={() => bulkPrepare(true)}
                disabled={bulkProcessing}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {bulkProcessing
                  ? '처리 중...'
                  : `엑셀 다운로드 + 상품 준비중 처리 (${selectedNewRows.length}건)`}
              </button>
              <button
                onClick={() => bulkPrepare(false)}
                disabled={bulkProcessing}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                준비중 처리만 ({selectedNewRows.length}건)
              </button>
            </>
          )}
          <button
            onClick={exportSelectedExcel}
            disabled={bulkProcessing}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            선택 엑셀 다운로드 ({selectedRows.length}건)
          </button>
          {selectedDeliveredRows.length > 0 && (
            <button
              onClick={openBulkDeliveredEdit}
              disabled={bulkProcessing}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {bulkProcessing
                ? '처리 중...'
                : `완료시각 일괄 변경 (${selectedDeliveredRows.length}건)`}
            </button>
          )}
          <button
            onClick={() => setSelectedIds(new Set())}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            선택 해제
          </button>
        </div>
      )}

      <div className="mb-2 flex items-center gap-3 text-xs text-gray-500" role="status">
        {loading
          ? '배송 목록을 불러오는 중입니다.'
          : deliveryError
          ? '배송 목록 조회 실패'
          : `조회 ${rows.length.toLocaleString()}건`}
        {selectedRows.length > 0 && (
          <span className="font-medium text-indigo-700">
            선택 {selectedRows.length.toLocaleString()}건
          </span>
        )}
      </div>

      {loading ? (
        <div className="center-box py-16">
          <Loading />
        </div>
      ) : deliveryError ? (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-white p-8 text-center"
        >
          <p className="text-sm text-red-700">
            배송 목록을 불러오지 못했습니다. 새로고침으로 다시 시도해주세요.
          </p>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          {statusFilter.length === 0
            ? '조회할 상태를 선택해주세요.'
            : hasCustomFilters
            ? '선택한 조건에 맞는 배송 건이 없습니다. 검색어나 필터를 확인해주세요.'
            : '처리할 배송 건이 없습니다.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="break-keep bg-gray-50 text-[11px] font-medium tracking-wide text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={allSelectableSelected}
                    ref={(input) => {
                      if (input)
                        input.indeterminate =
                          !allSelectableSelected &&
                          selectableRows.some((d) => selectedIds.has(d._id));
                    }}
                    aria-label="현재 목록 전체 선택"
                    onChange={toggleSelectAll}
                    disabled={selectableRows.length === 0}
                    title="배송지가 확정된 건 전체 선택 (주소입력대기·취소 제외)"
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40"
                  />
                </th>
                <th className="px-3 py-2 text-left">순번</th>
                {showDesiredDateUi && (
                  <th className="w-32 px-3 py-2 text-left">배송요청일</th>
                )}
                <th className="w-32 px-3 py-2 text-left">구매일</th>
                <th className="px-3 py-2 text-left">상품</th>
                {showOptionColumn && <th className="px-3 py-2 text-left">상품옵션</th>}
                <th className="px-3 py-2 text-left">수량</th>
                <th className="px-3 py-2 text-left">수령인</th>
                <th className="px-3 py-2 text-left">상태</th>
                <th className="px-3 py-2 text-left">송장 / 배송완료</th>
                <th className="px-3 py-2 text-left">처리 / 감사카드</th>
                <th className="px-3 py-2 text-left">코드</th>
                <th className="px-3 py-2 text-left">주문자</th>
                {showBrandUi && <th className="px-3 py-2 text-left">브랜드</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((d, idx) => {
                // 먼저 들어온(오래된) 주문이 1번. 목록은 최신순이므로 역순으로 번호 부여
                const seq = rows.length - idx;
                const orderer = getOrdererInfo(d);
                const badge = statusLabel[d.deliveryStatus] ?? {
                  text: d.deliveryStatus,
                  className: 'bg-gray-100 text-gray-600',
                };
                const canAdvance = !!nextStatusMap[d.deliveryStatus];
                const canRevert = !!prevStatusMap[d.deliveryStatus];
                // 신규는 작은 상태 배지로, 선택한 주문은 두 행을 함께 강조한다.
                const isNew = d.deliveryStatus === NEW_ORDER_STATUS;
                const rowClassName = selectedIds.has(d._id)
                  ? 'bg-indigo-50/70 hover:bg-indigo-50'
                  : 'hover:bg-gray-50';
                const recipient = d.recipient;
                const fullAddress = recipient
                  ? `(${recipient.postalCode}) ${recipient.address} ${
                      recipient.addressDetail || ''
                    }`.trim()
                  : '';
                const hasDesiredDate = !!toDeliveryDateKey(d.desiredDeliveryDate);
                const requestedAt = formatRelativeDateTime(recipient?.submittedAt);
                const completedAt =
                  d.deliveryStatus === GiftDeliveryStatus.Delivered
                    ? formatRelativeDateTime(d.shipping?.deliveredAt)
                    : null;
                return (
                  <Fragment key={d._id}>
                    <tr className={`align-middle ${rowClassName}`}>
                      {/* 0. 선택 체크박스 (배송지가 확정된 건만 — 주소입력대기·취소 제외) */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        {SELECTABLE_STATUSES.includes(d.deliveryStatus) ? (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(d._id)}
                            aria-label={`${recipient?.name || '수령인 미상'} 배송 선택`}
                            onChange={() => toggleSelect(d._id)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        ) : null}
                      </td>
                      {/* 1. 순번 (신규 주문은 좌측 강조선) */}
                      <td
                        className={`whitespace-nowrap px-3 py-2.5 text-sm font-bold text-black ${
                          isNew ? 'border-l-4 border-indigo-300' : ''
                        }`}
                      >
                        {seq}
                      </td>
                      {/* 받는 날은 배송요청보다 앞에 별도 컬럼으로 표시 */}
                      {showDesiredDateUi && (
                        <td className="px-3 py-2.5">
                          {hasDesiredDate ||
                          (datePolicyOf(d) &&
                            DESIRED_DATE_EDITABLE_STATUSES.includes(d.deliveryStatus)) ? (
                            <DesiredDeliveryDateBadge
                              date={d.desiredDeliveryDate}
                              late={d.desiredDeliveryDateLate}
                              requested={d.desiredDeliveryDateRequested}
                              edited={!!d.desiredDeliveryDateLogs?.length}
                              onEdit={
                                DESIRED_DATE_EDITABLE_STATUSES.includes(d.deliveryStatus)
                                  ? () => openDesiredDateEdit(d)
                                  : undefined
                              }
                              canSet={!!datePolicyOf(d)}
                              done={
                                d.deliveryStatus === GiftDeliveryStatus.Delivered ||
                                d.deliveryStatus === GiftDeliveryStatus.Canceled
                              }
                              prominent
                            />
                          ) : (
                            <span className="text-xs text-gray-400">미지정</span>
                          )}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        {requestedAt ? (
                          <div className="leading-relaxed">
                            <p className="text-sm font-medium text-gray-600">
                              {requestedAt.date}
                            </p>
                            <p className="text-xs text-gray-500">
                              {requestedAt.time} · {requestedAt.relative}
                            </p>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300">-</span>
                        )}
                      </td>
                      {/* 3. 상품 */}
                      <td className="px-3 py-2.5">
                        <p className="min-w-[140px] max-w-[220px] whitespace-normal break-words font-medium text-gray-800">
                          {getBrandItemName(d)}
                        </p>
                        <span className="text-[11px] text-gray-400">
                          {typeLabel[d.type] || d.type}
                        </span>
                      </td>
                      {/* 4. 상품옵션 (선택한 추가옵션 — 출고 시 어떤 옵션 상품을 보낼지 확인) */}
                      {showOptionColumn && (
                        <td className="px-3 py-2.5">
                          {d.selectedOptions && d.selectedOptions.length > 0 ? (
                            <ul className="max-w-[240px] space-y-1">
                              {d.selectedOptions.map((opt, i) => (
                                <li
                                  key={`${opt.optionName}-${opt.slotIndex ?? i}`}
                                  className="flex flex-wrap items-baseline gap-1.5 text-sm leading-tight"
                                >
                                  <span className="text-gray-500">
                                    {opt.optionName}
                                    {/* selectionCount>1: 슬롯 번호를 1부터 표시 (비누종류 1, 2 …) */}
                                    {opt.slotIndex !== undefined
                                      ? ` ${opt.slotIndex + 1}`
                                      : ''}
                                  </span>
                                  <span className="font-semibold text-indigo-600">
                                    {opt.choiceName}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-xs text-gray-300">-</span>
                          )}
                        </td>
                      )}
                      {/* 4. 수량 */}
                      <td className="whitespace-nowrap px-3 py-2.5 text-gray-700">
                        {d.quantity}개
                      </td>
                      {/* 5. 수령인 */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <p className="font-medium text-gray-800">
                          {d.recipient?.name || '-'}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatPhone(d.recipient?.phoneNumber)}
                        </p>
                      </td>
                      {/* 6. 상태 (신규 주문은 NEW 뱃지로 강조) */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <div className="flex items-center gap-1">
                          {isNew && (
                            <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">
                              NEW
                            </span>
                          )}
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                              isNew
                                ? 'bg-indigo-50 text-indigo-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {badge.text}
                          </span>
                        </div>
                        {isNew && (
                          <p className="mt-1 text-[10px] text-gray-500">
                            접수 대기중 · 바로 처리
                          </p>
                        )}
                      </td>
                      {/* 7. 송장 / 배송완료 */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        {/* 배송완료 건: 완료일 강조 (날짜 + 시각). 클릭 시 완료시각 수정 */}
                        {completedAt && (
                          <button
                            onClick={() => openDeliveredEdit(d)}
                            title="배송완료 시각 수정"
                            className="mb-1 block text-left text-sm font-bold leading-tight text-gray-900 underline decoration-dotted underline-offset-2 hover:text-indigo-600"
                          >
                            {completedAt.date}{' '}
                            <span className="text-xs font-medium text-gray-400">
                              {completedAt.time}
                            </span>
                          </button>
                        )}
                        {/* 송장/발송 정보 (배송중·배송완료) */}
                        {(d.deliveryStatus === GiftDeliveryStatus.Shipping ||
                          d.deliveryStatus === GiftDeliveryStatus.Delivered) && (
                          <div className="space-y-0.5 text-[11px] leading-tight text-gray-400">
                            {d.shipping?.carrier && (
                              <p className="text-gray-500">{d.shipping.carrier}</p>
                            )}
                            {d.shipping?.trackingNumber ? (
                              <button
                                onClick={() => openShippingEdit(d)}
                                title="송장 정보 수정"
                                className="block font-mono text-gray-500 underline decoration-dotted underline-offset-2 hover:text-gray-700"
                              >
                                {d.shipping.trackingNumber}
                              </button>
                            ) : (
                              <button
                                onClick={() => openShippingEdit(d)}
                                className="font-semibold text-amber-600 underline decoration-dotted underline-offset-2 hover:text-amber-700"
                              >
                                송장 미입력 (입력)
                              </button>
                            )}
                            {formatShortDateTime(d.shipping?.shippedAt) && (
                              <p>발송 {formatShortDateTime(d.shipping?.shippedAt)}</p>
                            )}
                          </div>
                        )}
                        {/* 그 외 상태: 표시할 배송정보 없음 */}
                        {d.deliveryStatus !== GiftDeliveryStatus.Shipping &&
                          d.deliveryStatus !== GiftDeliveryStatus.Delivered && (
                            <span className="text-xs text-gray-300">-</span>
                          )}
                      </td>
                      {/* 7. 처리 / 감사카드 */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {canAdvance ? (
                            <button
                              onClick={() => advanceStatus(d)}
                              disabled={processingId === d._id}
                              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                            >
                              {processingId === d._id
                                ? '처리중...'
                                : isNew
                                ? '주문접수 →'
                                : nextStatusLabel[d.deliveryStatus] || '상태 변경'}
                            </button>
                          ) : (
                            !canRevert && (
                              <span className="text-xs text-gray-300">대기</span>
                            )
                          )}
                          {canRevert && (
                            <button
                              onClick={() => revertStatus(d)}
                              disabled={revertingId === d._id}
                              title={prevStatusLabel[d.deliveryStatus]}
                              className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                            >
                              {revertingId === d._id ? '처리중...' : '되돌리기'}
                            </button>
                          )}
                          {/* CS 응대 이력 — 관리자는 작성까지, 공급사는 보기만.
                              ⚠️ 조회 축은 반드시 주문(giftOrder). CS 메모는 대부분 주문 단위로
                              저장되므로(메모장 안내: "입력은 주문 단위로 저장") 배송 축으로 물으면
                              그 배송에 직접 단 메모만 와서 대개 "없음"으로 보인다. 주문 축은 그
                              주문의 주문·쿠폰·배송 메모를 전부 실어준다. */}
                          {isAdminView ? (
                            <CsMemoButton
                              target={{ giftOrder: getOrderId(d) }}
                              title="주문 CS 응대 메모"
                              subtitle={csMemoSubtitle(d)}
                              memos={csMemos.memoMap.get(getOrderId(d))}
                              isLoading={csMemos.isLoading}
                              unavailable={csMemos.unavailable}
                              showPreview
                              notice="주문·쿠폰·배송 메모 통합 · 입력은 주문 단위로 저장"
                            />
                          ) : (
                            <BrandCsMemoButton
                              deliveryId={d._id}
                              subtitle={csMemoSubtitle(d)}
                            />
                          )}
                          {d.thanksPrint ? (
                            <button
                              onClick={() => setCardDelivery(d)}
                              className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                            >
                              감사카드
                            </button>
                          ) : (
                            // 카드 없이 들어온 건 — 고객 요청 시 운영자가 직접 붙인다 (admin 전용)
                            // 판정은 상품 단위(useThanksPrint ?? 브랜드 설정, 허용 디자인 유무) — 서버 주문 규칙과 같다
                            isAdminView &&
                            canAddThanksCard(d, brands) && (
                              <button
                                onClick={() => setCardDelivery(d)}
                                className="rounded-md border border-dashed border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-50"
                              >
                                + 감사카드
                              </button>
                            )
                          )}
                        </div>
                      </td>
                      {/* 8. 코드 */}
                      <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-gray-400">
                        {d.fullCode || d.code || '-'}
                      </td>
                      {/* 9. 주문자 */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <p className="text-gray-800">{orderer.name}</p>
                        <p className="text-xs text-gray-400">
                          {formatPhone(orderer.phone)}
                        </p>
                      </td>
                      {/* 10. 브랜드 (admin 또는 셀러가 여러 브랜드 담당 시 우측 참조열) */}
                      {showBrandUi && (
                        <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-600">
                          {typeof d.giftItemBrand === 'object'
                            ? d.giftItemBrand?.name
                            : '-'}
                        </td>
                      )}
                    </tr>
                    {/* 배송지: 선택 시 윗행과 동일 색으로 묶어 처리 대상을 구분한다 */}
                    <tr className={rowClassName}>
                      <td
                        colSpan={
                          11 +
                          (showDesiredDateUi ? 1 : 0) +
                          (showOptionColumn ? 1 : 0) +
                          (showBrandUi ? 1 : 0)
                        }
                        className={`px-3 py-2.5 align-middle ${
                          isNew
                            ? 'border-l-4 border-t border-dashed border-indigo-300 border-t-gray-200'
                            : 'border-t border-dashed border-gray-100'
                        }`}
                      >
                        {recipient?.address ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                              배송지
                            </span>
                            <p className="min-w-0 text-xs leading-snug text-gray-600">
                              ({recipient.postalCode}) {recipient.address}
                              {recipient.addressDetail
                                ? ` ${recipient.addressDetail}`
                                : ''}
                            </p>
                            <button
                              onClick={() => copyAddress(d._id, fullAddress)}
                              title="주소 복사"
                              className={`shrink-0 rounded-md border px-2 py-1 text-[11px] font-medium transition ${
                                copiedId === d._id
                                  ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                                  : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {copiedId === d._id ? '✓ 복사됨' : '주소 복사'}
                            </button>
                            {/* 배송지 정정은 관리자만 (공급사는 조회만) */}
                            {isAdminView && (
                              <button
                                onClick={() => openRecipientEdit(d)}
                                title="수령인·배송지 수정"
                                className="shrink-0 rounded-md border border-indigo-200 px-2 py-1 text-[11px] font-medium text-indigo-600 hover:bg-indigo-50"
                              >
                                배송지 수정
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                              배송지
                            </span>
                            <span className="text-xs text-gray-400">미입력</span>
                            {/* 주소 미입력 건도 관리자가 전화로 받아 직접 채울 수 있다 */}
                            {isAdminView && (
                              <button
                                onClick={() => openRecipientEdit(d)}
                                title="수령인·배송지 입력"
                                className="shrink-0 rounded-md border border-indigo-200 px-2 py-1 text-[11px] font-medium text-indigo-600 hover:bg-indigo-50"
                              >
                                배송지 입력
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 배송지 정정 모달 (admin 전용) */}
      {desiredDateTarget && (
        <DesiredDeliveryDateEditModal
          key={desiredDateTarget.deliveryId}
          target={desiredDateTarget}
          onClose={() => setDesiredDateTarget(null)}
          onSave={saveDesiredDate}
        />
      )}
      {recipientModal && (
        <RecipientEditModal
          modal={recipientModal}
          setModal={setRecipientModal}
          onSave={saveRecipient}
        />
      )}

      {/* 송장 입력 모달 */}
      {shippingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl">
            <h3 className="mb-4 text-base font-bold">
              {shippingModal.mode === 'edit' ? '송장 정보 수정' : '송장 정보 입력'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-semibold">택배사</label>
                <input
                  value={shippingModal.carrier}
                  onChange={(e) =>
                    setShippingModal({
                      ...shippingModal,
                      carrier: e.target.value,
                    })
                  }
                  placeholder="예: CJ대한통운"
                  className="w-full rounded border px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold">송장번호</label>
                <input
                  value={shippingModal.trackingNumber}
                  onChange={(e) =>
                    setShippingModal({
                      ...shippingModal,
                      trackingNumber: e.target.value,
                    })
                  }
                  placeholder="송장번호"
                  className="w-full rounded border px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setShippingModal(null)}
                className="flex-1 rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={submitShipping}
                disabled={processingId === shippingModal.deliveryId}
                className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {shippingModal.mode === 'edit' ? '저장' : '배송중으로 변경'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 배송완료 일시 입력 모달 */}
      {deliverModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !bulkProcessing && setDeliverModal(null)}
        >
          <div
            className="relative w-full max-w-md rounded-lg bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setDeliverModal(null)}
              aria-label="닫기"
              className="absolute right-3 top-3 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h3 className="mb-4 pr-8 text-base font-bold">
              {deliverModal.bulkIds
                ? `배송완료 시각 일괄 변경 (${deliverModal.bulkIds.length}건)`
                : deliverModal.isEdit
                ? '배송완료 시각 수정'
                : '배송완료 처리'}
            </h3>
            <div>
              <label className="mb-1 block text-sm font-semibold">배송완료 일시</label>
              <KstDateTimeField
                value={deliverModal.deliveredAt}
                onChange={(v) => setDeliverModal({ ...deliverModal, deliveredAt: v })}
              />
              <p className="mt-1 text-xs text-gray-400">
                {deliverModal.bulkIds
                  ? `선택한 ${deliverModal.bulkIds.length}건의 완료시각을 동일하게 변경합니다. (송장번호 등 다른 정보는 유지)`
                  : deliverModal.isEdit
                  ? '송장번호 등 다른 정보는 그대로 두고 완료시각만 변경합니다.'
                  : '기본값은 현재 시각이며, 실제 배송완료 시각으로 수정할 수 있습니다.'}
              </p>
            </div>
            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setDeliverModal(null)}
                className="flex-1 rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={submitDeliver}
                disabled={processingId === deliverModal.deliveryId || bulkProcessing}
                className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {deliverModal.bulkIds
                  ? `${deliverModal.bulkIds.length}건 적용`
                  : deliverModal.isEdit
                  ? '완료시각 저장'
                  : '배송완료로 변경'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 감사카드 미리보기 / 인쇄 */}
      <GiftBrandThanksCardModal
        delivery={cardDelivery}
        templates={thanksTemplates}
        onClose={() => setCardDelivery(null)}
        editable={isAdminView}
        brands={brands}
        onSaved={(id, thanksPrint) => {
          // 모달은 저장된 내용으로 바로 보여주고, 목록(같은 주문의 다른 건 포함)은 다시 불러온다
          setCardDelivery((prev) =>
            prev && prev._id === id ? { ...prev, thanksPrint } : prev,
          );
          refreshAll();
        }}
      />
    </div>
  );
};
